import { getCompactRangeSelectorOptions, getRangeSelector } from './rangeSelectorConfig';
import { yearGridlineXAxisOptions } from './featureFlags';

/**
 * Highcharts options for the Scores card (the Fundamentals card this shell
 * also served was deleted; see SCORE_CARDS.md §5). Same shell as the ratio
 * chart (own range presets, own zoom, own navigator) with a percent axis and
 * a fiscal-year tooltip. No axis sync with the other charts.
 */

const isShortLandscapeViewport = () => (
  typeof window !== 'undefined' && window.innerHeight <= 500
);

const isNarrowMobileViewport = () => (
  typeof window !== 'undefined' && window.innerWidth <= 768
);

const getPercentRangeSelector = () => {
  const base = getRangeSelector();
  if (!isNarrowMobileViewport()) return base;
  return { ...base, ...getCompactRangeSelectorOptions() };
};

export const ANNUAL_LINE_COLORS = {
  quality: '#2a78d6',
  growth: '#1baf7a',
  growthPerShare: '#eb6834',
  netMargin: '#2a78d6',
  ebitdaMargin: '#eda100',
  revenueGrowth: '#1baf7a',
  revenueGrowthPerShare: '#eb6834',
  shareGrowth: '#e34948',
  netDebtToValue: '#4a3aa7',
  fcfGrowth: '#008300',
  roa: '#4a3aa7',
  roic: '#e87ba4',
};

/** Fractions from the helpers → percent points. A missing year is null, not 0. */
export const toPercentPoints = (years, points) => {
  const byYear = new Map((points || []).map((point) => [point.year, point]));
  return (years || []).map(({ year, x }) => {
    const point = byYear.get(year);
    const fraction = point?.y;
    const y = typeof fraction === 'number' && Number.isFinite(fraction) ? fraction * 100 : null;
    return {
      x: point?.x ?? x,
      y,
      custom: {
        fiscalYear: point?.year ?? year,
        ...(point?.detail ? { detail: point.detail } : {}),
      },
    };
  });
};

export const hasPercentPoint = (data) => (
  (data || []).some((point) => point.y != null && Number.isFinite(point.y))
);

/** `unitSuffix` is per-series so the shared tooltip can serve both the percent
 *  cards and the Scores card's unitless 0–100 scores (pass ''). */
export const percentLine = (id, name, data, color, unitSuffix = '%') => ({
  id,
  name,
  data,
  color,
  custom: { unitSuffix },
  type: 'line',
  lineWidth: 2,
  connectNulls: false,
  showInLegend: true,
  marker: { enabled: true, radius: 3, symbol: 'circle' },
  dataGrouping: { enabled: false },
});

const percentTooltipFormatter = function percentTooltipFormatter() {
  const points = this.points || [];
  const anchor = points[0];
  if (!anchor) return false;
  const custom = anchor.point?.custom || anchor.point?.options?.custom || {};
  const fiscalYear = custom.fiscalYear ?? new Date(anchor.x).getUTCFullYear();
  const rows = points.map((point) => {
    const pointCustom = point.point?.custom || point.point?.options?.custom || {};
    const suffix = point.series.options.custom?.unitSuffix ?? '%';
    const value = point.y == null || Number.isNaN(point.y) ? '—' : `${Number(point.y).toFixed(2)}${suffix}`;
    const detail = pointCustom.detail ? `<br/>${pointCustom.detail}` : '';
    return `<span style="color:${point.color}">●</span> ${point.series.name}: <b>${value}</b>${detail}`;
  }).join('<br/>');
  return `<span style="font-size:11px">${fiscalYear}</span><br/>${rows}`;
};

export const getDefaultPercentChartOptions = (yAxisTitle = 'Percent') => ({
  yAxis: [{
    title: { text: yAxisTitle },
  }],
  xAxis: [{
    crosshair: false,
    ordinal: false,
    ...yearGridlineXAxisOptions(),
  }],
  chart: {
    animation: true,
    height: '35%',
    zIndex: 0,
    zoomType: 'x',
  },
  rangeSelector: getPercentRangeSelector(),
  responsive: {
    rules: [
      {
        condition: { maxWidth: 768 },
        chartOptions: {
          chart: { spacingLeft: 4, spacingRight: 4 },
          legend: {
            align: 'center',
            verticalAlign: 'bottom',
            itemDistance: 8,
            itemMarginBottom: 4,
            padding: 4,
            itemStyle: { fontSize: '11px' },
          },
          xAxis: [{ labels: { style: { fontSize: '10px' } } }],
          yAxis: [{ labels: { style: { fontSize: '10px' } } }],
          navigator: { enabled: false },
          scrollbar: { enabled: false },
        },
      },
      {
        condition: { maxWidth: 480 },
        chartOptions: { chart: { height: 320 } },
      },
      {
        condition: {
          minWidth: 601,
          maxWidth: 1024,
          callback: function landscape() { return isShortLandscapeViewport(); },
        },
        chartOptions: { chart: { height: 250 } },
      },
    ],
  },
  legend: { enabled: true },
  tooltip: {
    shared: true,
    formatter: percentTooltipFormatter,
  },
  plotOptions: {
    series: {
      dataGrouping: { enabled: false },
      connectNulls: false,
      states: {
        hover: { lineWidthPlus: 0 },
        inactive: { enabled: false },
      },
    },
  },
  series: [],
});
